import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Alert,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Chip,
  Avatar,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  CardActions,
  Badge,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import Pagination from "@mui/material/Pagination";
import {
  Close,
  FolderOpen,
  CloudUpload,
  CloudDownload,
  Person,
  MoreVert,
  Delete,
  Search,
  Business,
  Add,
  Check,
  TravelExplore,
  Group,
  Public,
  Send,
  Inbox,
  HourglassEmpty,
} from "@mui/icons-material";
import { hostname, parseError } from "../../../utils";
import axios from "axios";
import { FolderShare, ShareInvitation } from "../../../types";
import {
  FOLDER_SHARE_TYPE_COMPANY,
  FOLDER_SHARE_TYPE_DIRECT,
  FOLDER_SHARE_TYPE_PUBLIC,
  SHARE_INVITATION_STATUS_PENDING,
  SHARE_INVITATION_STATUS_ACCEPTED,
} from "../../../constants";
import { useWhitelabel } from "../../../context/WhitelabelContext";

interface SharedFoldersDialogProps {
  open: boolean;
  onClose: () => void;
  companyId: string;
  getToken: () => Promise<string>;
}

const SharedFoldersDialog: React.FC<SharedFoldersDialogProps> = ({
  open,
  onClose,
  companyId,
  getToken,
}) => {
  const { whitelabelSettings } = useWhitelabel();
  const publicFoldersEnabled = whitelabelSettings?.publicFolderFilter !== "none";
  const [activeTab, setActiveTab] = useState(0);
  const [outgoingShares, setOutgoingShares] = useState<FolderShare[]>([]);
  const [incomingShares, setIncomingShares] = useState<FolderShare[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<ShareInvitation[]>([]);
  const [browseShares, setBrowseShares] = useState<FolderShare[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedShare, setSelectedShare] = useState<FolderShare | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Browse tab state
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const perPage = 9;
  const [addingShareId, setAddingShareId] = useState<string | null>(null);
  const [addedShareIds, setAddedShareIds] = useState<Set<string>>(new Set());
  const [removingShareId, setRemovingShareId] = useState<string | null>(null);


  const fetchData = async () => {
    setLoading(true);
    setError("");

    try {
      const token = await getToken();

      // Fetch outgoing shares
      const outgoingResponse = await axios.get(
        `${hostname}/companies/${companyId}/folder-shares/outgoing`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setOutgoingShares(outgoingResponse.data.shares || []);

      // Fetch incoming accepted shares
      const incomingResponse = await axios.get(
        `${hostname}/companies/${companyId}/folder-shares/incoming`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setIncomingShares(incomingResponse.data.shares || []);

      // Fetch pending invitations
      const invitationsResponse = await axios.get(
        `${hostname}/companies/${companyId}/share-invitations/pending`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setPendingInvitations(invitationsResponse.data.invitations || []);
    } catch (err: any) {
      setError(parseError(err));
    } finally {
      setLoading(false);
    }
  };

  const fetchBrowseShares = async () => {
    setLoading(true);
    setError("");

    try {
      const token = await getToken();
      const response = await axios.get(`${hostname}/folder-shares/public`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page,
          per_page: perPage,
          search: search.trim() || undefined,
          exclude_company_id: companyId,
        },
      });

      setBrowseShares(response.data.shares || []);
      setTotal(response.data.total || 0);
    } catch (err: any) {
      setError(parseError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open]);

  useEffect(() => {
    if (open && activeTab === 3) {
      fetchBrowseShares();
    }
  }, [open, activeTab, page, search]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    share: FolderShare
  ) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedShare(share);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedShare(null);
  };

  const handleDeleteShare = async () => {
    if (!selectedShare) return;

    setDeleting(true);
    setError("");

    try {
      const token = await getToken();

      await axios.delete(`${hostname}/folder-shares/${selectedShare.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setOutgoingShares(outgoingShares.filter((s) => s.id !== selectedShare.id));
      handleMenuClose();
    } catch (err: any) {
      setError(parseError(err));
    } finally {
      setDeleting(false);
    }
  };

  const handleRespondToInvitation = async (
    invitation: ShareInvitation,
    accept: boolean
  ) => {
    try {
      const token = await getToken();
      await axios.post(
        `${hostname}/share-invitations/${invitation.id}/respond`,
        {
          companyId,
          accept,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Remove from pending
      setPendingInvitations(
        pendingInvitations.filter((inv) => inv.id !== invitation.id)
      );

      // Refresh data to update incoming shares if accepted
      if (accept) {
        fetchData();
      }
    } catch (err: any) {
      setError(parseError(err));
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(1);
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };

  const handleAddToKnowledgeBase = async (share: FolderShare) => {
    setAddingShareId(share.id);
    setError("");

    try {
      const token = await getToken();
      await axios.post(
        `${hostname}/folder-shares/public/${share.id}/add`,
        { companyId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setAddedShareIds((prev) => new Set(prev).add(share.id));
      // Refresh incoming shares to show the newly added folder
      fetchData();
    } catch (err: any) {
      setError(parseError(err));
    } finally {
      setAddingShareId(null);
    }
  };

  const handleRemoveIncomingShare = async (share: FolderShare) => {
    if (!share.invitationId) {
      setError("Cannot remove this share - missing invitation reference");
      return;
    }

    setRemovingShareId(share.id);
    setError("");

    try {
      const token = await getToken();
      await axios.delete(
        `${hostname}/share-invitations/${share.invitationId}/unsubscribe?companyId=${companyId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Remove from local state
      setIncomingShares(incomingShares.filter((s) => s.id !== share.id));
    } catch (err: any) {
      setError(parseError(err));
    } finally {
      setRemovingShareId(null);
    }
  };

  const totalPages = Math.ceil(total / perPage);

  const getShareTypeIcon = (shareType: string) => {
    switch (shareType) {
      case FOLDER_SHARE_TYPE_COMPANY:
        return <Group color="primary" />;
      case FOLDER_SHARE_TYPE_DIRECT:
        return <Send color="info" />;
      case FOLDER_SHARE_TYPE_PUBLIC:
        return <Public color="warning" />;
      default:
        return <FolderOpen />;
    }
  };

  const getShareTypeChip = (share: FolderShare) => {
    switch (share.shareType) {
      case FOLDER_SHARE_TYPE_COMPANY:
        return <Chip label="Company-wide" size="small" color="primary" />;
      case FOLDER_SHARE_TYPE_DIRECT:
        return (
          <Chip
            label={
              share.invitationStatus === SHARE_INVITATION_STATUS_PENDING
                ? "Pending"
                : share.targetCompany?.name || "Direct"
            }
            size="small"
            color={
              share.invitationStatus === SHARE_INVITATION_STATUS_PENDING
                ? "default"
                : "info"
            }
            icon={
              share.invitationStatus === SHARE_INVITATION_STATUS_PENDING ? (
                <HourglassEmpty />
              ) : undefined
            }
          />
        );
      case FOLDER_SHARE_TYPE_PUBLIC:
        return <Chip label="Public" size="small" color="warning" />;
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: "85vh",
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h6">Shared Folders</Typography>
            <Typography variant="body2" color="text.secondary">
              Manage folder sharing within and across companies
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{ mb: 2 }}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab
            icon={<CloudUpload />}
            iconPosition="start"
            label={`Outgoing (${outgoingShares.length})`}
          />
          <Tab
            icon={<CloudDownload />}
            iconPosition="start"
            label={`Incoming (${incomingShares.length})`}
          />
          <Tab
            icon={
              <Badge badgeContent={pendingInvitations.length} color="error">
                <Inbox />
              </Badge>
            }
            iconPosition="start"
            label="Invitations"
          />
          {publicFoldersEnabled && <Tab icon={<TravelExplore />} iconPosition="start" label="Browse Public" />}
        </Tabs>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="300px"
          >
            <Typography>Loading...</Typography>
          </Box>
        ) : (
          <>
            {/* Outgoing Tab */}
            {activeTab === 0 && (
              <Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Folders you're sharing. Company-wide shares are visible to all
                  users in your company. Direct shares require approval from the
                  receiving company. Public shares can be added to any company.
                </Alert>
                {outgoingShares.length === 0 ? (
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    minHeight="200px"
                  >
                    <FolderOpen
                      sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
                    />
                    <Typography variant="h6" color="text.secondary">
                      No shared folders
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Right-click a folder to share it
                    </Typography>
                  </Box>
                ) : (
                  <List>
                    {outgoingShares.map((share, index) => (
                      <React.Fragment key={share.id}>
                        {index > 0 && <Divider />}
                        <ListItem
                          alignItems="flex-start"
                          secondaryAction={
                            <IconButton
                              edge="end"
                              onClick={(e) => handleMenuOpen(e, share)}
                              disabled={deleting}
                            >
                              <MoreVert />
                            </IconButton>
                          }
                        >
                          <ListItemIcon>
                            {getShareTypeIcon(share.shareType)}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box
                                display="flex"
                                alignItems="center"
                                gap={1}
                                flexWrap="wrap"
                              >
                                <Typography variant="subtitle1">
                                  {share.folderName}
                                </Typography>
                                {getShareTypeChip(share)}
                              </Box>
                            }
                            secondary={
                              <>
                                <Typography
                                  component="span"
                                  variant="body2"
                                  color="text.secondary"
                                  display="block"
                                >
                                  {share.folderPath}
                                </Typography>
                                {share.description && (
                                  <Typography
                                    component="span"
                                    variant="body2"
                                    color="text.secondary"
                                    display="block"
                                    sx={{ mt: 0.5 }}
                                  >
                                    {share.description}
                                  </Typography>
                                )}
                              </>
                            }
                          />
                        </ListItem>
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </Box>
            )}

            {/* Incoming Tab */}
            {activeTab === 1 && (
              <Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Folders shared with your company by others.
                </Alert>
                {incomingShares.length === 0 ? (
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    minHeight="200px"
                  >
                    <FolderOpen
                      sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
                    />
                    <Typography variant="h6" color="text.secondary">
                      No incoming shares
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      When other companies share folders with you, they'll appear
                      here
                    </Typography>
                  </Box>
                ) : (
                  <List>
                    {incomingShares.map((share, index) => (
                      <React.Fragment key={share.id}>
                        {index > 0 && <Divider />}
                        <ListItem
                          alignItems="flex-start"
                          secondaryAction={
                            <IconButton
                              edge="end"
                              onClick={() => handleRemoveIncomingShare(share)}
                              disabled={removingShareId === share.id}
                              title="Remove from your knowledge base"
                              size="small"
                            >
                              <Close />
                            </IconButton>
                          }
                        >
                          <ListItemIcon>
                            <FolderOpen color="primary" />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box
                                display="flex"
                                alignItems="center"
                                gap={1}
                                flexWrap="wrap"
                              >
                                <Typography variant="subtitle1">
                                  {share.folderName}
                                </Typography>
                                <Chip
                                  label={share.ownerCompany?.name || "Unknown"}
                                  size="small"
                                  variant="outlined"
                                />
                              </Box>
                            }
                            secondary={
                              <>
                                <Typography
                                  component="span"
                                  variant="body2"
                                  color="text.secondary"
                                  display="block"
                                >
                                  {share.folderPath}
                                </Typography>
                                {share.description && (
                                  <Typography
                                    component="span"
                                    variant="body2"
                                    color="text.secondary"
                                    display="block"
                                    sx={{ mt: 0.5 }}
                                  >
                                    {share.description}
                                  </Typography>
                                )}
                                {share.sharedByUser && (
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                    sx={{ mt: 1 }}
                                  >
                                    <Avatar
                                      src={share.sharedByUser.photo || undefined}
                                      sx={{ width: 20, height: 20 }}
                                    >
                                      {!share.sharedByUser.photo && (
                                        <Person sx={{ fontSize: 14 }} />
                                      )}
                                    </Avatar>
                                    <Typography
                                      component="span"
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Shared by{" "}
                                      {share.sharedByUser.displayName ||
                                        share.sharedByUser.email ||
                                        "Unknown"}
                                    </Typography>
                                  </Box>
                                )}
                              </>
                            }
                          />
                        </ListItem>
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </Box>
            )}

            {/* Pending Invitations Tab */}
            {activeTab === 2 && (
              <Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Share invitations waiting for your approval. Accept to add the
                  folder to your incoming shares.
                </Alert>
                {pendingInvitations.length === 0 ? (
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    minHeight="200px"
                  >
                    <Inbox
                      sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
                    />
                    <Typography variant="h6" color="text.secondary">
                      No pending invitations
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      When companies want to share folders with you, invitations
                      will appear here
                    </Typography>
                  </Box>
                ) : (
                  <List>
                    {pendingInvitations.map((invitation, index) => (
                      <React.Fragment key={invitation.id}>
                        {index > 0 && <Divider />}
                        <ListItem
                          alignItems="flex-start"
                          secondaryAction={
                            <Box display="flex" gap={1}>
                              <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                onClick={() =>
                                  handleRespondToInvitation(invitation, true)
                                }
                              >
                                Accept
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                onClick={() =>
                                  handleRespondToInvitation(invitation, false)
                                }
                              >
                                Decline
                              </Button>
                            </Box>
                          }
                        >
                          <ListItemIcon>
                            <Send color="info" />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box
                                display="flex"
                                alignItems="center"
                                gap={1}
                                flexWrap="wrap"
                              >
                                <Typography variant="subtitle1">
                                  {invitation.folderName}
                                </Typography>
                                <Chip
                                  label={
                                    invitation.sourceCompany?.name || "Unknown"
                                  }
                                  size="small"
                                  variant="outlined"
                                />
                              </Box>
                            }
                            secondary={
                              <>
                                <Typography
                                  component="span"
                                  variant="body2"
                                  color="text.secondary"
                                  display="block"
                                >
                                  {invitation.folderPath}
                                </Typography>
                                {invitation.message && (
                                  <Typography
                                    component="span"
                                    variant="body2"
                                    color="text.secondary"
                                    display="block"
                                    sx={{ mt: 0.5, fontStyle: "italic" }}
                                  >
                                    "{invitation.message}"
                                  </Typography>
                                )}
                                {invitation.invitedByUser && (
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                    sx={{ mt: 1 }}
                                  >
                                    <Avatar
                                      src={
                                        invitation.invitedByUser.photo ||
                                        undefined
                                      }
                                      sx={{ width: 20, height: 20 }}
                                    >
                                      {!invitation.invitedByUser.photo && (
                                        <Person sx={{ fontSize: 14 }} />
                                      )}
                                    </Avatar>
                                    <Typography
                                      component="span"
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Invited by{" "}
                                      {invitation.invitedByUser.displayName ||
                                        invitation.invitedByUser.email ||
                                        "Unknown"}
                                    </Typography>
                                  </Box>
                                )}
                              </>
                            }
                          />
                        </ListItem>
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </Box>
            )}

            {/* Browse Public Tab */}
            {activeTab === 3 && (
              <Box>
                <Box sx={{ mb: 3 }}>
                  <TextField
                    fullWidth
                    placeholder="Search public folders..."
                    value={search}
                    onChange={handleSearchChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                {browseShares.length === 0 ? (
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    minHeight="300px"
                  >
                    <FolderOpen
                      sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
                    />
                    <Typography variant="h6" color="text.secondary">
                      No public folders found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {search
                        ? "Try adjusting your search query"
                        : "No public folders are available yet"}
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Grid container spacing={2}>
                      {browseShares.map((share) => (
                        <Grid item xs={12} sm={6} md={4} key={share.id}>
                          <Card
                            sx={{
                              height: "100%",
                              display: "flex",
                              flexDirection: "column",
                            }}
                          >
                            <CardContent sx={{ flexGrow: 1 }}>
                              <Box display="flex" alignItems="flex-start" mb={2}>
                                <Public
                                  sx={{
                                    fontSize: 40,
                                    color: "warning.main",
                                    mr: 1,
                                  }}
                                />
                                <Box flex={1}>
                                  <Typography variant="h6" gutterBottom noWrap>
                                    {share.folderName}
                                  </Typography>
                                </Box>
                              </Box>

                              <Divider sx={{ my: 1 }} />

                              {share.description && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{
                                    mb: 2,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    display: "-webkit-box",
                                    WebkitLineClamp: 3,
                                    WebkitBoxOrient: "vertical",
                                    minHeight: "3.6em",
                                  }}
                                >
                                  {share.description}
                                </Typography>
                              )}

                              <Box
                                display="flex"
                                flexDirection="column"
                                gap={1}
                                mt={2}
                              >
                                <Box display="flex" alignItems="center" gap={1}>
                                  {share.ownerCompany?.logoUrl ? (
                                    <Avatar
                                      src={share.ownerCompany.logoUrl}
                                      sx={{ width: 24, height: 24 }}
                                    />
                                  ) : (
                                    <Business sx={{ fontSize: 20 }} />
                                  )}
                                  <Box>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      {share.ownerCompany?.name ||
                                        "Unknown Company"}
                                    </Typography>
                                    {share.contactEmail && (
                                      <Typography
                                        variant="caption"
                                        display="block"
                                        color="text.secondary"
                                      >
                                        {share.contactEmail}
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                              </Box>
                            </CardContent>
                            <CardActions sx={{ p: 2, pt: 0 }}>
                              <Button
                                fullWidth
                                size="small"
                                variant={addedShareIds.has(share.id) ? "outlined" : "contained"}
                                color="primary"
                                disabled={addingShareId === share.id || addedShareIds.has(share.id)}
                                onClick={() => handleAddToKnowledgeBase(share)}
                                startIcon={addedShareIds.has(share.id) ? <Check /> : <Add />}
                              >
                                {addingShareId === share.id
                                  ? "Adding..."
                                  : addedShareIds.has(share.id)
                                  ? "Added"
                                  : "Add to Knowledge Base"}
                              </Button>
                            </CardActions>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>

                    {totalPages > 1 && (
                      <Box display="flex" justifyContent="center" mt={3}>
                        <Pagination
                          count={totalPages}
                          page={page}
                          onChange={handlePageChange}
                          color="primary"
                        />
                      </Box>
                    )}
                  </>
                )}
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleDeleteShare} disabled={deleting}>
          <ListItemIcon>
            <Delete fontSize="small" />
          </ListItemIcon>
          <ListItemText>{deleting ? "Deleting..." : "Stop Sharing"}</ListItemText>
        </MenuItem>
      </Menu>
    </Dialog>
  );
};

export default SharedFoldersDialog;
